import React from 'react'
import { Input } from './input'
import { formatCPF, formatCNPJ, formatCEP } from '@/lib/utils/validation'

interface MaskedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  mask?: 'cpf' | 'cnpj' | 'cep' | 'phone-br'
  onValueChange?: (value: string) => void
}

export const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ mask, onValueChange, onChange, value, ...props }, ref) => {
    const applyMask = (val: string): string => {
      if (!mask || !val) return val

      const cleaned = val.replace(/[^\d]/g, '')

      switch (mask) {
        case 'cpf':
          if (cleaned.length <= 11) {
            return formatCPF(cleaned)
          }
          return formatCPF(cleaned.slice(0, 11))

        case 'cnpj':
          if (cleaned.length <= 14) {
            return formatCNPJ(cleaned)
          }
          return formatCNPJ(cleaned.slice(0, 14))

        case 'cep':
          if (cleaned.length <= 8) {
            return formatCEP(cleaned)
          }
          return formatCEP(cleaned.slice(0, 8))

        case 'phone-br':
          if (cleaned.length <= 10) {
            return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
          }
          return cleaned.slice(0, 11).replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3')

        default:
          return val
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      const maskedValue = applyMask(rawValue)

      e.target.value = maskedValue

      if (onValueChange) {
        const cleanValue = maskedValue.replace(/[^\d]/g, '')
        onValueChange(cleanValue)
      }

      if (onChange) {
        onChange(e)
      }
    }

    const displayValue = React.useMemo(() => {
      return applyMask(String(value || ''))
    }, [value, mask])

    return (
      <Input
        {...props}
        ref={ref}
        value={displayValue}
        onChange={handleChange}
      />
    )
  }
)

MaskedInput.displayName = 'MaskedInput'
